import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Heart, Globe, Users, Sparkles, ArrowRight } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageShell, {
  PageHero,
  LightSection,
  DarkSection,
  SectionHead,
  FeatureCard,
  ClosingBanner,
  PrimaryCTA,
  SecondaryCTA,
} from '@/components/PageShell';

const ABOUT_DESCRIPTION =
  "Learn about Sport Fund — our story, mission, and values. We're building a platform to help athletes worldwide raise support for training, equipment, travel and competition.";

export default function About() {
  usePageMeta('About', ABOUT_DESCRIPTION);
  const { t } = useTranslation();
  return (
    <PageShell
      hero={
        <PageHero
          eyebrow="Our story"
          title={
            <>
              Built so athletes never have to{' '}
              <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-300 bg-clip-text text-transparent">
                chase their goals alone.
              </span>
            </>
          }
          lead={t('aboutPage.hero.subtitle')}
          size="md"
        />
      }
    >
      {/* Story */}
      <LightSection>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <SectionHead eyebrow="The origin" title={t('aboutPage.ourStory.title')} />
          </div>
          <div className="lg:col-span-8 space-y-6 text-lg text-slate-600 leading-relaxed">
            <p>{t('aboutPage.ourStory.paragraph1')}</p>
            <p>{t('aboutPage.ourStory.paragraph2')}</p>
            <p>{t('aboutPage.ourStory.paragraph3')}</p>
          </div>
        </div>
      </LightSection>

      {/* Mission */}
      <DarkSection>
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHead tone="dark" eyebrow="Our mission" title={t('aboutPage.ourMission.title')} />
          </div>
          <div className="lg:col-span-7 space-y-5 text-lg text-slate-300 leading-relaxed">
            <p className="text-xl text-white font-medium">{t('aboutPage.ourMission.paragraph1')}</p>
            <p>{t('aboutPage.ourMission.paragraph2')}</p>
          </div>
        </div>
      </DarkSection>

      {/* Values bento */}
      <LightSection bg="gray">
        <SectionHead
          eyebrow="What drives us"
          title={t('aboutPage.values.title')}
          lead="Four operating principles, applied to every product and pricing decision we make."
        />
        <div className="mt-14 grid md:grid-cols-2 gap-5">
          <FeatureCard
            icon={<Heart className="h-6 w-6" />}
            title={t('aboutPage.values.faith.title')}
            body={t('aboutPage.values.faith.description')}
          />
          <FeatureCard
            icon={<Globe className="h-6 w-6" />}
            title={t('aboutPage.values.globalCommunity.title')}
            body={t('aboutPage.values.globalCommunity.description')}
          />
          <FeatureCard
            icon={<Users className="h-6 w-6" />}
            title={t('aboutPage.values.authenticConnection.title')}
            body={t('aboutPage.values.authenticConnection.description')}
          />
          <FeatureCard
            icon={<Sparkles className="h-6 w-6" />}
            title={t('aboutPage.values.simplicityExcellence.title')}
            body={t('aboutPage.values.simplicityExcellence.description')}
          />
        </div>
      </LightSection>

      {/* Vision */}
      <LightSection>
        <div className="max-w-4xl mx-auto text-center">
          <SectionHead align="center" eyebrow="Vision" title={t('aboutPage.vision.title')} />
          <p className="mt-8 text-lg md:text-xl text-slate-600 leading-relaxed">{t('aboutPage.vision.paragraph1')}</p>
          <p className="mt-5 text-lg md:text-xl text-slate-900 font-medium leading-relaxed">{t('aboutPage.vision.paragraph2')}</p>
        </div>
      </LightSection>

      <ClosingBanner
        eyebrow="Ready to join us?"
        title={
          <>
            Your community is your first sponsor.{' '}
            <span className="text-slate-300">Set up the page that lets them back you.</span>
          </>
        }
        primary={
          <Link to="/signup-creator">
            <PrimaryCTA>
              {t('aboutPage.cta.missionButton')} <ArrowRight className="h-5 w-5" />
            </PrimaryCTA>
          </Link>
        }
        secondary={
          <Link to="/how-it-works">
            <SecondaryCTA>How it works</SecondaryCTA>
          </Link>
        }
      />
    </PageShell>
  );
}
